'use client';
import LegacyAvailability from './legacy-availability/legacy-availability';
import { AvailabilityWrapperProps } from './types';
import ModernAvailability from './modern-availability/modern-availability';
import { useProductDetailPageContext } from 'app/[lang]/[esite]/(core)/product/[product]/context/product-detail';
import { usePriceAndAvailabilityQuery } from 'services/priceAvailability/use-query-price-availability';
import { PCC_AVAILABLE_TOOLTIP_DISCLAIMER_TYPE } from 'constants/messageCode';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import { Conditional } from '@cat-ecom/pcc-components';
import { useEffect } from 'react';
import { CatSkeleton } from 'components/blocks-components';

const AvailabilityWrapper = ({
  partsList,
  dealerInformation,
  isDeliveryEnabled,
  dealerData,
  showSourcingDetailToggle,
  hideLabel,
  availRemoveInterimStockFlag,
  availabilityByStoreLink,
  isLoggedIn,
  isPDPPage,
  cutoffTimesData,
  isPCC_PDP_headerUpdateMobileView,
  priceData: serverSidePriceData,
  isHeadlessPriceAvailability
}: AvailabilityWrapperProps) => {
  const { address } = dealerData;
  const { quantity, storeId, langId, locale, setPartsList, quantityUpdatedAt } =
    useProductDetailPageContext();

  const partsListWithQuantity = partsList.map((item) => ({
    ...item,
    quantity
  }));

  const clientSideFetching = quantityUpdatedAt;

  const {
    data: priceInformation,
    isLoading,
    isFetching
  } = usePriceAndAvailabilityQuery({
    // The combination of initialData and refetchOnMount instructs useQuery
    // to use server-side data on the first render.
    // When the user changes the quantity and quantityUpdatedAt is set,
    // initialData is removed to enable client-side fetching.
    ...(clientSideFetching ? {} : { initialData: serverSidePriceData }),
    storeId,
    langId,
    partsList: partsListWithQuantity,
    staleTime: 10 * 60 * 1000, // 10 minutes
    refetchOnMount: false,
    isHeadlessPriceAvailability
  });

  useEffect(() => {
    if (setPartsList && partsList) {
      setPartsList(partsList);
    }
  }, [partsList, setPartsList]);

  const availabilityData = priceInformation?.items?.find(
    (item) => item.price
  )?.availability;
  const {
    storeFeatures: { isStoreAvailabilityEnabled, isPickupEnabled },
    featureFlags: {
      PCC_availRemoveInterimStockFlag,
      PCC_showPickupCutoffFlag,
      PCC_pdpEstimatedDeliveryDateFlag
    }
  } = dealerInformation;

  const { ectMessages, hoseAssemblyECTmsg } = dealerData;
  const { messages = [] } = ectMessages;
  const message = messages.find(
    ({ type }) => type === PCC_AVAILABLE_TOOLTIP_DISCLAIMER_TYPE
  );
  const messageDetails = message?.messageDetails;
  const availabilityToolTipMessage = messageDetails?.find(
    (message) => message.locale === locale
  )?.text;
  const product = priceInformation?.items?.at(0);
  const sourcingDetails = product?.sourcingDetails;
  const hoseAvailabilityLabel = isHeadlessPriceAvailability
    ? hoseAssemblyECTmsg?.text
    : product?.hoseAvailabilityLabel;
  const hoseAssemblyIndicator = product?.hoseAssemblyIndicator;
  const hoseAssemblyDetails = { hoseAvailabilityLabel, hoseAssemblyIndicator };
  const indirectReplacement = product?.partLevelFlags?.isIndirectReplacement;
  const replacementParts = product?.replacementParts;
  const isReplacedPart = !isEmpty(replacementParts) || indirectReplacement;
  const unitPrice = product?.price?.unitPrice;
  const showPriceAndAvailability = unitPrice && !isReplacedPart;
  const storeAvailability = product?.storeAvailability;
  const deliveryAvailability = product?.deliveryAvailability;

  if (isFetching) {
    return <CatSkeleton className="cat-u-display-block" size="xl" />;
  }
  return (
    <Conditional test={showPriceAndAvailability}>
      {isStoreAvailabilityEnabled ? (
        <ModernAvailability
          availabilityData={availabilityData}
          availabilityToolTipMessage={availabilityToolTipMessage}
          isPickupEnabled={isPickupEnabled}
          isDeliveryEnabled={isDeliveryEnabled}
          showSourcingDetailToggle={showSourcingDetailToggle}
          hideLabel={hideLabel}
          sourcingDetails={sourcingDetails}
          hoseAssemblyDetails={hoseAssemblyDetails}
          address={address}
          locale={locale}
          isLoading={isLoading}
          availRemoveInterimStockFlag={availRemoveInterimStockFlag}
          storeAvailability={storeAvailability}
          PCC_availRemoveInterimStockFlag={PCC_availRemoveInterimStockFlag}
          showAdditionalInfo
          hideToolTipIcon
          availabilityByStoreLink={availabilityByStoreLink}
          showPickupCutoffFlag={PCC_showPickupCutoffFlag}
          isPDPPage={isPDPPage}
          pdpEstimatedDeliveryDateFlag={PCC_pdpEstimatedDeliveryDateFlag}
          isLoggedIn={isLoggedIn}
          cutoffTimesData={cutoffTimesData}
          deliveryAvailability={deliveryAvailability}
          isPCC_PDP_headerUpdateMobileView={isPCC_PDP_headerUpdateMobileView}
        />
      ) : (
        <LegacyAvailability
          availabilityData={availabilityData}
          isPickupEnabled={isPickupEnabled}
          isDeliveryEnabled={isDeliveryEnabled}
          dealerData={dealerData}
          isLoading={isLoading}
          showAdditionalInfo
          availabilityToolTipMessage={availabilityToolTipMessage}
          hoseAssemblyDetails={hoseAssemblyDetails}
          hideToolTipIcon
        />
      )}
    </Conditional>
  );
};

export default AvailabilityWrapper;
